import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { faHistory, IconDefinition } from '@fortawesome/pro-duotone-svg-icons';
import { Actions, ofActionCompleted, Store } from '@ngxs/store';
import { CellClickEvent, GridComponent, GridDataResult, GridItem, SelectionEvent } from '@progress/kendo-angular-grid';
import { TooltipDirective } from '@progress/kendo-angular-tooltip';
import _ from 'lodash';
import { combineLatest, iif, Observable, of, Subject, timer } from 'rxjs';
import { debounceTime, distinctUntilChanged, filter, map, skip, switchMap, takeUntil, tap, withLatestFrom } from 'rxjs/operators';
import { BasicBrickComponent } from 'src/app/components/bricks/basic-brick-component';
import { SamwebBrick } from 'src/app/components/bricks/samweb-brick.decorator';
import { getTopmostTableCellElement, isTargetingGridHeader } from 'src/app/helper/elementAncestorFunctions';
import { WellKnownIconsEnum } from 'src/app/helper/iconHelper';
import { isAnonymous } from 'src/app/helper/peerHelper';
import { shortcutStringBuilder } from 'src/app/helper/shortcutStringBuilder';
import { BrickCategory } from 'src/app/samwebDescription/samweb/brickCategoryType';
import { CallHistoryFilterType } from 'src/app/samwebDescription/samweb/callHistory/callHistoryFilterType';
import { Key } from 'src/app/samwebDescription/samweb/shortcuts/Keys';
import { ShortcutDescription } from 'src/app/samwebDescription/samweb/shortcuts/shortcutDescription';
import { ShortcutTypeGlobal } from 'src/app/samwebDescription/samweb/shortcuts/shortcutTypeGlobal';
import { FeatureEnum } from 'src/app/samwebDescription/samwin/FeatureEnum';
import {
   CallHistoryAllFilterCleared,
   CallHistoryBrowserMakeCall,
   CallHistoryChangeDataPage,
   CallHistoryClearAndStartLoadingEntries,
   CallHistoryEntrySelected,
   CallHistoryFiltersChanged,
   CallHistoryProgressStateMemoChangedByServer,
   CallHistoryScrollTopChanged,
   CallHistorySearchChangedThroughIncomingCall,
   CallHistorySearchChangedThroughIncomingCallFinished,
   CallHistorySearchValueChanged,
   CallHistorySelectNextEntry,
   RemoveSpecifiedCallHistoryData,
} from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/call-history-browser-behaviour/call-history-browser-behaviour.actions';
import { CallHistoryBrowserBehaviourState } from 'src/app/store/dashboard/brick-wall/brick-behaviour-state/brickTypes/call-history-browser-behaviour/call-history-browser-behaviour.state';
import { CallHistoryBrowserSettingsState } from 'src/app/store/dashboard/brick-wall/brick-settings/specific-brick-settings/call-history-browser-settings/call-history-browser-settings.state';
import { DashboardState } from 'src/app/store/dashboard/dashboard.state';
import { StartLoadingChatDetailsByUid } from 'src/app/store/samwin/chats/chats.actions';
import { ChatId } from 'src/app/store/samwin/chats/models/chat-id';
import { StartNewCallAction } from 'src/app/store/samwin/client-phone/client-phone.actions';
import { ClientPhoneState } from 'src/app/store/samwin/client-phone/client-phone.state';
import { NextDialTargetChanged } from 'src/app/store/samwin/next-dial-target/next-dial-target.actions';
import { SamwinLinesStateSelector } from 'src/app/store/samwin/samwin-lines/samwin-lines.selector';
import { SamwinSkillsState } from 'src/app/store/samwin/samwin-skills/samwin-skills.state';
import { EnabledFeaturesState } from 'src/app/store/session/enabled-features/enabled-features.state';
import { SettingsSelectors } from 'src/app/store/settings/settings.selectors';
import { ConversationTypeRemoteDto } from 'src/generated/signalR/conversation-type-remote-dto';
import { CallHistoryEntryRemoteDto } from 'src/generated/swagger';
import { CallHistoryBrowserSettingsComponent } from './call-history-browser-settings/call-history-browser-settings.component';

@Component({
   selector: 'samweb-call-history-browser-brick',
   templateUrl: './call-history-browser-brick.component.html',
   styleUrls: ['./call-history-browser-brick.component.scss'],
   changeDetection: ChangeDetectionStrategy.OnPush,
})
@SamwebBrick({
   brickDefinitionId: 'callHistoryBrowser',
   brickTitleTranslationKey: 'brick.brick_definitions.call_history_browser.title',
   brickDescriptionTranslationKey: 'brick.brick_definitions.call_history_browser.description',
   brickIcon: faHistory,
   minColWidth: 10,
   minRowHeight: 10,
   brickCategory: BrickCategory.CALLING,
   settingsComponent: CallHistoryBrowserSettingsComponent,
   settingStateHandler: CallHistoryBrowserSettingsState,
})
export class CallHistoryBrowserBrickComponent extends BasicBrickComponent implements OnInit, AfterViewInit, OnDestroy {
   get gridContent(): Element {
      return _(this.element.nativeElement.getElementsByClassName('k-grid-content')).head();
   }
   get gridDisplayHeight(): number {
      return this.element.nativeElement.querySelector('.k-virtual-content')?.offsetHeight;
   }
   get gridRowHeight(): number {
      return this.element.nativeElement.querySelector('td')?.offsetHeight ?? 0;
   }
   // To avoid unexpected behavior during scrolling, set pageSize to at least three times the number of the visible Grid elements.
   // The number of the visible Grid elements is determined by the height and rowHeight settings of the Grid.
   get gridPageSize(): number {
      const pageSize = _.round((this.gridDisplayHeight / this.gridRowHeight) * 3);
      return _.isNaN(pageSize) ? 5 : pageSize;
   }

   private destroy$ = new Subject();
   private lastClickedCell: CellClickEvent;

   public clearFilterIcon: IconDefinition = WellKnownIconsEnum.CLEAR.iconSmall;
   public filterIcon: IconDefinition = WellKnownIconsEnum.FILTER.icon;
   public statusBarFilterIcon: IconDefinition = WellKnownIconsEnum.FILTER.iconSmall;
   public resyncIcon: IconDefinition = WellKnownIconsEnum.RESYNC.iconSmall;

   public skip$: Observable<number>;
   public callHistoryData$: Observable<GridDataResult>;
   public searchValue$: Observable<string>;
   public availableFilters$ = of(CallHistoryFilterType.values());
   public selectedFilters = new Array<CallHistoryFilterType>();
   public showPrimaryFullDateFormat$: Observable<boolean>;
   public newDataAvailable$: Observable<boolean>;
   public selectedKeys$: Observable<string[]>;
   public currentSearchValue: string[];
   @ViewChild('cellTooltipDirective')
   public cellTooltipDirective: TooltipDirective;
   @ViewChild('kendoGrid')
   private kendoGrid: GridComponent;
   @ViewChild('dataUpdateTooltipDirective')
   private dataUpdateTooltipDirective: TooltipDirective;
   public keyActions: ShortcutDescription[] = [];

   constructor(private store: Store, private element: ElementRef, private actions$: Actions) {
      super();
   }

   ngOnInit(): void {
      this.callHistoryData$ = this.store.select(CallHistoryBrowserBehaviourState.getCallHistoryValuesForBrick).pipe(
         map((filterFn) => filterFn(this.brickInstanceId)),
         map((x: GridDataResult) => {
            return {
               data: _(x.data)
                  .orderBy((y) => y.timestamp, 'desc')
                  .value(),
               total: x.total,
            };
         })
      );

      this.skip$ = this.store.select(CallHistoryBrowserBehaviourState.getCallHistorySkipForBrick).pipe(map((fn) => fn(this.brickInstanceId)));

      this.newDataAvailable$ = this.store
         .select(CallHistoryBrowserBehaviourState.getCallHistoryNewDataAvailableForBrick)
         .pipe(map((fn) => fn(this.brickInstanceId)));

      this.searchValue$ = this.store.select(CallHistoryBrowserBehaviourState.getSearchValueFn).pipe(map((filterFn) => filterFn(this.brickInstanceId)));

      this.showPrimaryFullDateFormat$ = this.store
         .select(CallHistoryBrowserSettingsState.getShowPrimaryFullDateFormat)
         .pipe(map((fn) => fn(this.brickInstanceId)));

      this.store
         .select(CallHistoryBrowserSettingsState.getAutomaticFilteringOnIncomingCall)
         .pipe(
            map((fn) => fn(this.brickInstanceId)),
            switchMap((isAutomaticFilteringOnIncomingCall) =>
               iif(
                  () => isAutomaticFilteringOnIncomingCall,
                  this.store.select(SamwinLinesStateSelector.isPhoneRinging).pipe(
                     filter((isRinging) => isRinging),
                     switchMap(() => {
                        const currentCallShortInfo = this.store.selectSnapshot(SamwinLinesStateSelector.getCurrentCallShortInfo);
                        let searchValue = `${currentCallShortInfo.peerName} ${currentCallShortInfo.peerId}`;
                        if (currentCallShortInfo.peerId === 'anonymous') {
                           searchValue = `${currentCallShortInfo.peerId}`;
                        }
                        return this.store.dispatch(new CallHistorySearchChangedThroughIncomingCall(this.brickInstanceId, searchValue.trim()));
                     })
                  ),
                  of(null)
               )
            ),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.store
         .select(SamwinSkillsState.getLoggedOnCallAndChatSkillIds)
         .pipe(
            distinctUntilChanged((prev, curr) => _.isEqual(prev, curr)),
            switchMap(() => {
               const selectedFilters = this.store.selectSnapshot(CallHistoryBrowserBehaviourState.getCallHistorySelectedFiltersFn)(this.brickInstanceId);
               if (selectedFilters.includes(CallHistoryFilterType.ONLY_ACTIVE_SKILLS)) {
                  return this.store.dispatch(new CallHistoryClearAndStartLoadingEntries(this.brickInstanceId));
               }
               return of(null);
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.store
         .select(SamwinLinesStateSelector.isCallInProgress)
         .pipe(
            skip(1), // avoid being triggered by initial emit (change from no value to false)
            distinctUntilChanged(),
            filter((isCallInProgress) => !isCallInProgress),
            switchMap(() => this.store.dispatch(new CallHistorySearchChangedThroughIncomingCallFinished(this.brickInstanceId))),
            takeUntil(this.destroy$)
         )
         .subscribe();

      this.selectedKeys$ = this.store.select(CallHistoryBrowserBehaviourState.getSelectedEntryId).pipe(
         map((getSelectedEntry) => getSelectedEntry(this.brickInstanceId)),
         map((selectedEntry) => [selectedEntry])
      );

      this.store
         .select(CallHistoryBrowserBehaviourState.getCallHistorySelectedFiltersFn)
         .pipe(takeUntil(this.destroy$))
         .subscribe((e) => {
            this.selectedFilters = e(this.brickInstanceId);
         });

      this.searchValue$
         .pipe(
            tap((directorySearchValue) => {
               this.currentSearchValue = _.split(directorySearchValue, ' ');
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();

      // publish Next Dial Target when the selected entry changes
      this.selectedKeys$
         .pipe(
            withLatestFrom(this.callHistoryData$),
            distinctUntilChanged(([prevSelectedKeys, prevData], [curSelectedKeys, curData]) => {
               const prevSelectedEntry = _.find(prevData.data, (x) => x.uniqueCallId === _.first(prevSelectedKeys));
               const curSelectedEntry = _.find(curData.data, (x) => x.uniqueCallId === _.first(curSelectedKeys));
               return prevSelectedEntry?.peerId === curSelectedEntry?.peerId;
            }),
            tap(([selectedKeys, data]) => {
               const isFocussed = this.store.selectSnapshot(DashboardState.isBrickFocussedFn)(this.brickInstanceId);
               if (!isFocussed) {
                  return;
               }

               const selectedEntry = _.find(data.data, (x) => x.uniqueCallId === _.first(selectedKeys));
               const numberToCall = _.isNil(selectedEntry) ? this.currentSearchValue.join(' ') : selectedEntry.peerId;
               const setDialTarget = !isAnonymous(numberToCall) && selectedEntry?.conversationType === ConversationTypeRemoteDto.Call;
               this.store.dispatch(
                  new NextDialTargetChanged(
                     {
                        number: setDialTarget ? numberToCall : '',
                        name: setDialTarget ? selectedEntry?.peerName : '',
                     },
                     this.brickInstanceId
                  )
               );
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();

      // (re-)publish Next Dial Target on focus
      this.store
         .select(DashboardState.isBrickFocussedFn)
         .pipe(
            map((fn) => fn(this.brickInstanceId)),
            takeUntil(this.destroy$)
         )
         .subscribe((hasFocus) => {
            if (hasFocus) {
               const selectedEntry = this.store.selectSnapshot(CallHistoryBrowserBehaviourState.getSelectedEntry)(this.brickInstanceId);
               const numberToCall = _.isNil(selectedEntry) ? this.currentSearchValue.join(' ') : selectedEntry.peerId;
               const setDialTarget = !isAnonymous(numberToCall) && selectedEntry?.conversationType === ConversationTypeRemoteDto.Call;
               this.store.dispatch(
                  new NextDialTargetChanged(
                     {
                        number: setDialTarget ? numberToCall : '',
                        name: setDialTarget ? selectedEntry?.peerName : '',
                     },
                     this.brickInstanceId
                  )
               );
            }
         });

      this.store
         .select(EnabledFeaturesState.isFeatureEnabled(FeatureEnum.ALLOW_TO_SEE_FOREIGN_HISTORY))
         .pipe(
            switchMap((isAllowed) =>
               isAllowed
                  ? of(null)
                  : this.actions$.pipe(
                       ofActionCompleted(CallHistoryProgressStateMemoChangedByServer),
                       map((action) =>
                          this.store.selectSnapshot(CallHistoryBrowserBehaviourState.getForeignCallHistoryEntries)(action.action.data.changedEntries)
                       ),
                       filter((foreignCallHistoryEntries) => !_.isEmpty(foreignCallHistoryEntries)),
                       tap((foreignCallHistoryEntries) =>
                          this.store.dispatch(new RemoveSpecifiedCallHistoryData(this.brickInstanceId, foreignCallHistoryEntries))
                       )
                    )
            ),
            takeUntil(this.destroy$)
         )
         .subscribe();

      combineLatest([
         of({
            shortcutKey: shortcutStringBuilder(Key.ArrowUp),
            action: () => {
               console.debug('[HotKey] CallHistory Pressed ArrowUp');
               this.store.dispatch(new CallHistorySelectNextEntry(this.brickInstanceId, 'up'));
               this.scrollToSelectedRow();
               return true;
            },
         }),
         of({
            shortcutKey: shortcutStringBuilder(Key.ArrowDown),
            action: () => {
               console.debug('[HotKey] CallHistory Pressed ArrowDown');
               this.store.dispatch(new CallHistorySelectNextEntry(this.brickInstanceId, 'down'));
               this.scrollToSelectedRow();
               return true;
            },
         }),
         of({
            shortcutKey: shortcutStringBuilder(Key.PageUp),
            action: () => {
               console.debug('[HotKey] CallHistory Pressed PageUp');
               this.store.dispatch(new CallHistorySelectNextEntry(this.brickInstanceId, 'up', this.gridPageSize));
               this.scrollToSelectedRow();
               return true;
            },
         }),
         of({
            shortcutKey: shortcutStringBuilder(Key.PageDown),
            action: () => {
               console.debug('[HotKey] CallHistory Pressed PageDown');
               this.store.dispatch(new CallHistorySelectNextEntry(this.brickInstanceId, 'down', this.gridPageSize));
               this.scrollToSelectedRow();
               return true;
            },
         }),
         this.getShortcutDescription$(this.store.select(SettingsSelectors.getHotkeyForAction).pipe(map((fn) => fn(ShortcutTypeGlobal.MAKE_CALL.value))), () => {
            const doDialAndAnswerShareHotkeys = this.store.selectSnapshot(SettingsSelectors.doDialAndAnswerShareHotkeys);
            const isCallRingingAndAcceptable = this.store.selectSnapshot(ClientPhoneState.isCallRingingAndAcceptable);
            if (doDialAndAnswerShareHotkeys && isCallRingingAndAcceptable) {
               return false;
            }
            console.debug('[HotKey] CallHistory Pressed MAKE_CALL');
            this.store.dispatch(new CallHistoryBrowserMakeCall(this.brickInstanceId));
            return true;
         }),
      ])
         .pipe(
            tap((shortcuts) => {
               this.keyActions = shortcuts;
            }),
            takeUntil(this.destroy$)
         )
         .subscribe();
   }

   ngAfterViewInit(): void {
      const isSearching$ = this.store.select(CallHistoryBrowserBehaviourState.isSearching).pipe(
         map((filterFn) => filterFn(this.brickInstanceId)),
         distinctUntilChanged((prev, curr) => _.isEqual(prev, curr))
      );
      const currentTake$ = timer(0, 500).pipe(
         switchMap(() => of(this.gridPageSize)),
         distinctUntilChanged((prev, curr) => _.isEqual(prev, curr)),
         debounceTime(500)
      );

      isSearching$
         .pipe(
            switchMap((isSearching) =>
               this.kendoGrid.pageChange.pipe(
                  filter(() => !isSearching),
                  debounceTime(500),
                  switchMap((state) => this.store.dispatch(new CallHistoryChangeDataPage(this.brickInstanceId, state.skip, state.take)))
               )
            ),
            takeUntil(this.destroy$)
         )
         .subscribe();

      isSearching$
         .pipe(
            switchMap((isSearching) =>
               currentTake$.pipe(
                  filter(() => !isSearching),
                  tap((gridPageSize: number) => this.store.dispatch(new CallHistoryChangeDataPage(this.brickInstanceId, null, gridPageSize)))
               )
            ),
            takeUntil(this.destroy$)
         )
         .subscribe();

      timer(0, 500)
         .pipe(
            switchMap(() => of(this.gridContent?.scrollTop)),
            distinctUntilChanged(),
            tap((scrollTop: number) => this.store.dispatch(new CallHistoryScrollTopChanged(this.brickInstanceId, scrollTop))),
            takeUntil(this.destroy$)
         )
         .subscribe();
   }

   ngOnDestroy(): void {
      this.destroy$.next();
      this.destroy$.complete();
   }

   onRowDoubleClick(event: MouseEvent) {
      if (isTargetingGridHeader(event)) {
         return;
      }

      const entry = this.store.selectSnapshot(CallHistoryBrowserBehaviourState.getSelectedEntry)(this.brickInstanceId);
      if (
         entry.conversationType &&
         (entry.conversationType === ConversationTypeRemoteDto.Chat || entry.conversationType === ConversationTypeRemoteDto.RocketChat)
      ) {
         // chat or Rocket.Chat
         this.store.dispatch(new StartLoadingChatDetailsByUid(new ChatId(0, '', entry.uniqueCallId), true));
      } else {
         const field = this.lastClickedCell.column.field;
         let numberToCall = '';
         if (field === 'forwardingPeer') {
            numberToCall = entry?.forwardingPeerId;
         } else if (field === 'transfer_target') {
            numberToCall = entry?.targetPeerId;
         }
         if (numberToCall === '') {
            numberToCall = entry?.peerId;
         }
         if (!isAnonymous(numberToCall)) {
            // call branch
            this.store.dispatch(new StartNewCallAction(numberToCall));
         }
      }
   }

   public trackBy(index: number, item: GridItem): any {
      const data = item.data as CallHistoryEntryRemoteDto;
      return data.id;
   }

   searchTextChanged(value: string) {
      this.store.dispatch(
         new NextDialTargetChanged(
            {
               number: value,
            },
            this.brickInstanceId
         )
      );
      this.store.dispatch(new CallHistorySearchValueChanged(this.brickInstanceId, value));
   }

   removeAllFilters() {
      this.store.dispatch(new CallHistoryAllFilterCleared(this.brickInstanceId));
   }

   filterChanged() {
      this.store.dispatch(new CallHistoryFiltersChanged(this.brickInstanceId));
   }

   selectionChanged(event: SelectionEvent) {
      const selectedRow = _.first(event.selectedRows);
      if (_.isNil(selectedRow)) {
         return;
      }
      const selectedEntry = selectedRow.dataItem as CallHistoryEntryRemoteDto;
      this.store.dispatch(new CallHistoryEntrySelected(this.brickInstanceId, selectedEntry.uniqueCallId));
   }

   scrollToSelectedRow() {
      const index = this.store.selectSnapshot(CallHistoryBrowserBehaviourState.getSelectedEntryIndexOverAllPages)(this.brickInstanceId);
      const selectedRowPosition = this.gridRowHeight * index;
      const halfWindowHeight = this.gridDisplayHeight / 2 - this.gridRowHeight / 2;
      const scrollPosition = selectedRowPosition - halfWindowHeight;
      this.gridContent.scrollTo({ top: scrollPosition });
   }

   public showTooltip(e: MouseEvent): void {
      const element = e.target as HTMLElement;
      const tooltipAnchor = getTopmostTableCellElement(element);
      if (tooltipAnchor && (tooltipAnchor.scrollWidth > tooltipAnchor.parentElement.offsetWidth || tooltipAnchor.offsetWidth < tooltipAnchor.scrollWidth)) {
         this.cellTooltipDirective.toggle(tooltipAnchor);
      } else {
         this.cellTooltipDirective.hide();
      }
   }

   public hideTooltip() {
      this.cellTooltipDirective.hide();
   }

   public applyDataUpdate() {
      this.dataUpdateTooltipDirective.hide();
      this.store.dispatch(new CallHistoryClearAndStartLoadingEntries(this.brickInstanceId));
   }

   kendoCellClick(event: CellClickEvent) {
      this.lastClickedCell = event;
   }

   call(numberToCall: any) {
      this.store.dispatch(new StartNewCallAction(numberToCall.toString()));
   }

   isCallable(numberToCall: string, conversationType: ConversationTypeRemoteDto) {
      return conversationType === ConversationTypeRemoteDto.Call && !isAnonymous(numberToCall);
   }
}
